import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, FileText, Calendar, Hash, IndianRupee, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getInvoiceBySalesOrder } from '@/app/services/documentLinkService'
import { format, parseISO } from 'date-fns'

interface LinkedInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  salesOrderId: number
  salesOrderNumber: string
}

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(Number(amount))
}

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'dd MMM yyyy')
  } catch (error) {
    return String(dateString)
  }
}

// Detail item component
interface DetailItemProps {
  icon: React.ElementType
  label: string
  value: string | React.ReactNode
  className?: string
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className = '' }) => (
  <div className={`flex items-start gap-3 ${className}`}>
    <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
    <div className="flex-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  </div>
)

export const LinkedInvoiceModal: React.FC<LinkedInvoiceModalProps> = ({
  isOpen,
  onClose,
  salesOrderId,
  salesOrderNumber
}) => {
  const navigate = useNavigate()

  // Fetch linked invoice details
  const { 
    data: linkedInvoice, 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['linked-invoice-modal', salesOrderId],
    queryFn: () => getInvoiceBySalesOrder(salesOrderId),
    enabled: isOpen && !!salesOrderId
  })

  const handleViewInvoice = () => {
    if (linkedInvoice?.voucher_id) {
      onClose()
      navigate(`/sales-invoices/detail/${linkedInvoice.voucher_id}`)
    }
  }

  // Get status badge variant
  const getStatusBadgeVariant = () => {
    if (!linkedInvoice) return 'secondary'
    
    switch (linkedInvoice.payment_status?.toLowerCase()) {
      case 'paid':
        return 'success'
      case 'partially paid':
        return 'warning'
      case 'unpaid':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Linked Invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Sales Order Reference */}
          <div className="text-sm text-muted-foreground">
            Sales Order: <span className="font-medium text-foreground">{salesOrderNumber}</span>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error State */}
          {isError && !linkedInvoice && (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No linked invoice found</p>
            </div>
          )}

          {/* Invoice Details */}
          {linkedInvoice && !isLoading && (
            <div className="bg-muted/30 rounded-lg p-4 space-y-4">
              {/* Invoice Number and Status */}
              <div className="flex items-start justify-between">
                <DetailItem
                  icon={Hash}
                  label="Invoice Number"
                  value={
                    <span className="text-base font-semibold">
                      {linkedInvoice.voucher_number}
                    </span>
                  }
                />
                <Badge variant={getStatusBadgeVariant() as any}>
                  {linkedInvoice.payment_status || linkedInvoice.status || 'Unknown'}
                </Badge>
              </div>

              {/* Date and Amount */}
              <div className="grid grid-cols-2 gap-4">
                <DetailItem
                  icon={Calendar}
                  label="Invoice Date"
                  value={formatDate(linkedInvoice.transaction_date)}
                />
                <DetailItem
                  icon={IndianRupee}
                  label="Total Amount"
                  value={formatCurrency(linkedInvoice.total_amount)}
                />
              </div>

              {/* Linked Date */}
              {linkedInvoice.linked_at && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Linked on {formatDate(linkedInvoice.linked_at)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {linkedInvoice && (
            <Button onClick={handleViewInvoice}>
              View Invoice Details
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}